import React, { useState } from 'react';
import { prepareNinjaTraderOAuth } from '../../services/vectorDashboardApi';
import { formatLastSynced } from './clientReportingFreshness';

// Re-exported so existing importers keep working; the implementation lives in
// clientReportingFreshness, which is pure and testable without React.
export { formatLastSynced };

function bannerTitle(rows) {
  if (rows.length > 1) return `${rows.length} broker connections need attention`;
  const row = rows[0];
  if (row.broker === 'alpaca') return `${row.label} API keys invalid`;
  return `${row.label} session expired`;
}

function bannerCopy(viewingOther, error) {
  if (error) return error;
  if (viewingOther) {
    return 'This account stopped syncing when its broker connection lapsed. '
      + 'Only the account holder can reconnect it — let them know.';
  }
  return 'Trades stopped syncing when the broker connection lapsed. '
    + 'Reconnecting restores it — resyncing alone will not.';
}

/**
 * "Your session expired — reconnect" prompt for NinjaTrader and Alpaca.
 *
 * Shown only when the server says `needsReauth`. One row per lapsed connection,
 * each with its own action: OAuth for NinjaTrader, API key update for Alpaca.
 */
export default function ClientReportingReauthBanner({
  health,
  onDismiss,
  viewingOther = false,
  onUpdateAlpacaKeys,
}) {
  const [busyKey, setBusyKey] = useState(null);
  const [error, setError] = useState('');

  if (!health?.needsReauth) return null;

  const broken = (health.connections || []).filter((c) => c.needsReauth);
  const rows = broken.length
    ? broken
    : [{ broker: 'ninjatrader', mode: 'live', label: 'NinjaTrader', modeLabel: 'Live' }];

  const reconnectNinja = async (mode) => {
    const key = `ninjatrader-${mode}`;
    setBusyKey(key);
    setError('');
    try {
      const frontendUrl = `${window.location.origin}/client-reporting`;
      const res = await prepareNinjaTraderOAuth({
        frontendUrl,
        environment: mode === 'demo' ? 'demo' : 'live',
      });
      if (!res?.authUrl) throw new Error(res?.error || 'Could not start Tradovate sign-in');
      window.location.href = res.authUrl;
    } catch (e) {
      setError(e.message || 'Could not start Tradovate sign-in');
      setBusyKey(null);
    }
  };

  const rowKey = (row) => `${row.broker || 'ninjatrader'}-${row.mode || 'live'}`;

  return (
    <div className="vd-cr-reauth" role="alert">
      <div className="vd-cr-reauth-head">
        <span className="vd-cr-reauth-title">{bannerTitle(rows)}</span>
        <span className="vd-cr-reauth-copy">{bannerCopy(viewingOther, error)}</span>
      </div>

      <ul className="vd-cr-reauth-list">
        {rows.map((row) => {
          const key = rowKey(row);
          const isAlpaca = row.broker === 'alpaca';
          return (
            <li key={key} className="vd-cr-reauth-row">
              <span className="vd-cr-reauth-account">
                {row.label}
                {row.modeLabel ? (
                  <span className={`vd-cr-reauth-mode vd-cr-reauth-mode--${row.mode}`}>
                    {row.modeLabel}
                  </span>
                ) : null}
                <span className="vd-cr-reauth-synced">
                  last synced {formatLastSynced(row.lastSyncedAt)}
                </span>
              </span>
              {viewingOther ? (
                <span className="vd-cr-reauth-readonly">Needs reconnecting</span>
              ) : isAlpaca ? (
                <button
                  type="button"
                  className="vd-cr-reauth-btn"
                  onClick={() => onUpdateAlpacaKeys?.()}
                  disabled={busyKey !== null}
                >
                  Update API keys
                </button>
              ) : (
                <button
                  type="button"
                  className="vd-cr-reauth-btn"
                  onClick={() => reconnectNinja(row.mode)}
                  disabled={busyKey !== null}
                >
                  {busyKey === key ? 'Opening…' : `Reconnect ${row.modeLabel || ''}`.trim()}
                </button>
              )}
            </li>
          );
        })}
      </ul>

      {onDismiss ? (
        <button type="button" className="vd-cr-reauth-dismiss" onClick={onDismiss}>
          Not now
        </button>
      ) : null}
    </div>
  );
}
